import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { SeatManagementPanel } from "@/pages/crm/SeatManagementPanel";
import { ws, ROLE_LABELS } from "./api";
import { useWorkspace } from "./WorkspaceContext";
import { PageHeader, SubTabs, Panel, Loading, Field, NativeSelect } from "./ui";

const ROLE_HELP = {
  business_owner: "Full control including billing and seats.",
  business_admin: "Manages organisation, employees and roles (except admins).",
  manager: "Full CRM access; sees team reports and full records of direct reports.",
  sales_user: "Full CRM access including import/export.",
  employee: "Uses CRM and own space; cannot delete records or bulk import/export.",
};

const General = () => {
  const { isAdmin } = useWorkspace();
  const [prefix, setPrefix] = useState("");
  const [s, setS] = useState(null);
  useEffect(() => { ws("/crm/workspace/organization").then((o) => { setS(o.settings); setPrefix(o.settings.employee_id_prefix); }).catch((e) => toast.error(e.message)); }, []);
  if (!s) return <Loading />;
  const save = async () => { try { const r = await ws("/crm/workspace/settings", { method: "PUT", body: { employee_id_prefix: prefix } }); setS(r); setPrefix(r.employee_id_prefix); toast.success("Employee ID prefix saved"); } catch (e) { toast.error(e.message); } };
  return (
    <Panel title="Employee IDs" testid="settings-employee-ids">
      <p className="text-sm text-slate-500 mb-4">New employees get an automatic ID such as <span className="font-mono text-slate-800" data-testid="emp-id-preview">{(prefix || "EMP").toUpperCase()}{String(s.next_number || 1).padStart(3, "0")}</span>. You can still type a custom ID when adding an employee.</p>
      <div className="flex gap-2 items-end max-w-sm">
        <div className="flex-1"><Field label="ID prefix"><Input value={prefix} maxLength={8} disabled={!isAdmin} onChange={(e) => setPrefix(e.target.value.toUpperCase())} data-testid="emp-prefix-input" /></Field></div>
        {isAdmin && <Button onClick={save} disabled={!prefix} className="bg-[#0B1530] hover:bg-[#1a2650]" data-testid="emp-prefix-save-btn">Save</Button>}
      </div>
    </Panel>
  );
};

const Roles = () => {
  const { isAdmin, role, me, refresh } = useWorkspace();
  const [members, setMembers] = useState(null);
  const load = () => ws("/crm/workspace/members").then((r) => setMembers(r.members)).catch((e) => toast.error(e.message));
  useEffect(() => { load(); }, []);
  const change = async (m, r) => {
    try { await ws(`/crm/workspace/members/${m.crm_user_id}/role`, { method: "PATCH", body: { crm_role: r } }); toast.success(`${m.name} is now ${ROLE_LABELS[r]}`); load(); if (m.crm_user_id === me.crm_user_id) refresh(); }
    catch (e) { toast.error(e.message); }
  };
  if (!members) return <Loading />;
  const options = Object.keys(ROLE_LABELS).filter((r) => r !== "business_owner" && (r !== "business_admin" || role === "business_owner")).map((r) => ({ value: r, label: ROLE_LABELS[r] }));
  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <Panel title="Members & roles" className="lg:col-span-2" testid="settings-roles">
        <ul className="divide-y divide-slate-100 -my-2">{members.map((m) => {
          const locked = !isAdmin || m.crm_role === "business_owner" || (role !== "business_owner" && m.crm_role === "business_admin");
          return (
            <li key={m.crm_user_id} className="py-3 flex flex-col sm:flex-row sm:items-center gap-2" data-testid={`member-row-${m.crm_user_id}`}>
              <div className="flex-1 min-w-0"><p className="text-sm font-medium">{m.name}</p><p className="text-xs text-slate-500">{m.email}</p></div>
              {locked ? <span className="text-xs rounded-full bg-slate-100 px-2.5 py-1" data-testid={`member-role-${m.crm_user_id}`}>{ROLE_LABELS[m.crm_role]}</span>
                : <div className="w-full sm:w-56"><NativeSelect value={m.crm_role} onChange={(v) => v && change(m, v)} options={options} placeholder="Role" testid={`member-role-select-${m.crm_user_id}`} /></div>}
            </li>
          );
        })}</ul>
      </Panel>
      <Panel title="What each role can do">
        <ul className="space-y-3">{Object.entries(ROLE_HELP).map(([r, h]) => <li key={r}><p className="text-sm font-medium">{ROLE_LABELS[r]}</p><p className="text-xs text-slate-500">{h}</p></li>)}</ul>
      </Panel>
    </div>
  );
};

export default function SettingsPage() {
  const { tab = "general" } = useParams();
  const { me } = useWorkspace();
  const isOwnerBusiness = me?.crm_role === "business_owner" && me?.account_type === "business";
  const tabs = [
    { key: "general", label: "General", to: "/crm-app/settings/general" },
    { key: "roles", label: "Roles & Access", to: "/crm-app/settings/roles" },
    ...(isOwnerBusiness ? [{ key: "seats", label: "Seats & Billing", to: "/crm-app/settings/seats" }] : []),
  ];
  return (
    <div data-testid="settings-page">
      <PageHeader title="Settings" subtitle="Workspace configuration" />
      <SubTabs tabs={tabs} />
      {tab === "general" && <General />}
      {tab === "roles" && <Roles />}
      {tab === "seats" && isOwnerBusiness && <div className="-mx-4 sm:-mx-6 lg:-mx-10"><SeatManagementPanel /></div>}
    </div>
  );
}
